import styled from 'styled-components';
import { usePatternContext } from '../../../../../context/PatternContext';
import { AnalysisItem } from './data';
import { s } from './ResultReport.styles';
import GraphCircle from './GraphCircle';
import { gradeInfo } from '../../formatOptions';
import { useNavigate } from 'react-router-dom';

interface Props {
  data: AnalysisItem[];
}

const Summary = ({ data }: Props) => {
  const navigate = useNavigate();
  const { patternInfo } = usePatternContext();
  const totalCnt = Number(data?.[0]?.total_cnt ?? 0); // 전체물건
  const winCnt = Number(data?.[1]?.total_cnt ?? 0); // 수익물건

  const formatValue = (grade: number) => {
    switch (grade) {
      case 1:
        return { color: '#EC0010', text: '매우 낮음' };
      case 2:
        return { color: '#EC5717', text: '낮음' };
      case 3:
        return { color: '#27A131', text: '보통' }; // 보통
      case 4:
        return { color: '#008AFF', text: '높음' }; // 높음
      case 5:
        return { color: '#008AFF', text: '매우 높음' }; // 매우 높음
      default:
        return { color: '#e5e8ea', text: '데이터없음' }; // 데이터 없음
    }
  };
  return (
    <s.Section>
      <s.TitleBox>
        <s.SectionTitle>종합분석</s.SectionTitle>
        <s.Guide>
          과거 수십년간의 경매 낙찰, 낙찰 후 매각 데이터를 분석해 수익과 손실을
          구분하고 각 물건의 안전성, 시세차익가능성, 수익성을 분석한 결과입니다.
        </s.Guide>
      </s.TitleBox>

      <Inner>
        <GraphCircle totalCnt={totalCnt} winCnt={winCnt} />
        <Cards>
          {['전체물건', '수익(Win)물건', '손실(Lose)물건'].map(
            (title, index) => {
              const item = data[index];

              if (!item) return null;

              return (
                <Card key={index}>
                  <CardTitle>
                    <TitleBox>
                      <Title>{title}</Title>
                      <Grade $color={gradeInfo[item?.safety ?? 0]?.color}>
                        {gradeInfo[item.safety ?? 0]?.title ?? '데이터없음'}
                      </Grade>
                    </TitleBox>
                    <CardCount>
                      경매건수: {Number(item.total_cnt).toLocaleString()}건
                    </CardCount>
                  </CardTitle>

                  <CardContent>
                    {(
                      [
                        [item.safety, '안전성'],
                        [item.profit_possibility, '시세차익가능성'],
                        [item.profitability, '수익성'],
                      ] as [number, string][]
                    ).map(([value, label], idx) => {
                      const { color, text } = formatValue(value);
                      return (
                        <GraphBox key={idx}>
                          <GraphTitleBox>
                            <GraphTitle>{label}</GraphTitle>
                            <GraphTitle $color={color}>{text}</GraphTitle>
                          </GraphTitleBox>
                          <Bar>
                            <Fill $width={value * 20} $color={color} />
                          </Bar>
                        </GraphBox>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            },
          )}
        </Cards>
      </Inner>

      <s.SearchButton
        onClick={() => {
          const stateToSend = {
            selectedItem: patternInfo,
          };

          navigate('/aiRecommendation', {
            state: stateToSend,
          });
        }}
      >
        <img src="/assets/images/icon/search_white.svg" alt="" />이 알고리즘으로
        물건 검색
      </s.SearchButton>
    </s.Section>
  );
};

export default Summary;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const Cards = styled.div`
  width: calc(100vw - 16px);
  display: flex;
  align-items: flex-start;
  gap: 24px;
  overflow-x: auto;
  padding-right: 32px;
`;

const Card = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 282px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const CardTitle = styled.div`
  display: flex;
  padding: 0 8px;
  justify-content: space-between;
  align-items: flex-end;
  align-self: stretch;
`;

const TitleBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Grade = styled.div<{ $color?: string }>`
  color: ${({ $color }) => $color};
  font-size: 14px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const CardCount = styled.div`
  color: #616161;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const CardContent = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #e5e8ea;
  background: #fafafb;
`;

const GraphBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const GraphTitleBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const GraphTitle = styled.div<{ $color?: string }>`
  color: ${({ $color }) => ($color ? $color : '#222')};
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;
`;

const Bar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 999px;
  background: rgba(112, 115, 124, 0.08);
`;

const Fill = styled.div<{
  $width: number;
  $color?: string;
}>`
  width: ${({ $width }) => `${$width}%`};
  height: 4px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
`;
